import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import TitlePage from '../../components/TitlePage';
import H1Styled from '../../components/Typography/H1Styled';
import PrimaryButton from '../../components/Buttons/PrimaryButton';
import TextareaPresentational from '../../components/Form/TextAreaPresentational';
import BaseModal from '../../components/Modals/BaseModal';
import InlineBadge from '../../components/InlineBadge';
import FidelityForm from '../../components/Fidelity/FidelityForm';
import FidelityContractSection from '../../components/Fidelity/FidelityContractSection';

import { useAuth } from '../../contexts/Auth';
import {
  getFidelity,
  updateFidelity,
  approveFidelity,
  rejectFidelity,
  validateFidelityCard,
} from '../../api/fidelity';
import { FidelityRequest, FidelityStatus } from '../../interfaces/fidelity';
import { getStatusLabel, getStatusColor } from '../../constants/fidelity';
import {
  fidelityValidator,
  fidelityRejectValidator,
} from '../../validators/fidelity';
import useYupValidationResolver from '../../libs/YupValidationResolver';
import {
  isDelegato,
  isDelegatoRegionale,
  isDelegatoSezione,
  isSegreteriaNazionale,
} from '../../libs/utils/auth';
import {
  COORDINATES_NOT_FOUND_WARNING,
  withResolvedCoordinates,
} from '../../libs/utils/geocoding';

const ModificaFidelity = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [{ token, profile }] = useAuth();

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [tesseraWarning, setTesseraWarning] = useState<string | null>(null);

  const isReadOnly = isDelegatoRegionale(profile);
  const canApprove =
    isSegreteriaNazionale(profile) || isDelegatoSezione(profile);

  const { data: fidelity, isLoading } = useQuery(
    ['fidelity', id],
    () => getFidelity({ id: id!, token }),
    {
      enabled: !!id,
    }
  );

  const defaultValues: Partial<FidelityRequest> = {
    nome: '',
    ragioneSociale: '',
    partitaIva: '',
    telefono: '',
    telefonoSecondario: '',
    email: '',
    sito: '',
    indirizzo: '',
    cap: '',
    citta: '',
    provincia: '',
    descrizione: '',
    guidaProsit: false,
    note: '',
    categorie: [
      {
        categoria: '',
        sottocategoria: '',
        scontoOnline: 0,
        scontoNegozio: 0,
      },
    ],
    socioOnav: false,
    numeroTessera: undefined,
    sezione: undefined,
    stato: FidelityStatus.BOZZA,
    scadenzaContratto: new Date().toISOString(),
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<FidelityRequest>({
    resolver: useYupValidationResolver(fidelityValidator),
    defaultValues,
  });

  useEffect(() => {
    if (fidelity?.data) {
      const data = fidelity.data.attributes;
      const formData = {
        nome: data.nome,
        ragioneSociale: data.ragioneSociale,
        partitaIva: data.partitaIva,
        telefono: data.telefono,
        telefonoSecondario: data.telefonoSecondario || '',
        email: data.email,
        sito: data.sito || '',
        indirizzo: data.indirizzo,
        cap: data.cap,
        citta: data.citta,
        provincia: data.provincia,
        descrizione: data.descrizione || '',
        guidaProsit: Boolean((data as any).guidaProsit),
        note: data.note || '',
        categorie:
          data.categorie.length > 0
            ? data.categorie.map((categoria) => ({
                categoria: categoria.categoria,
                sottocategoria: categoria.sottocategoria,
                scontoOnline: Number(categoria.scontoOnline),
                scontoNegozio: Number(categoria.scontoNegozio),
              }))
            : [
                {
                  categoria: '',
                  sottocategoria: '',
                  scontoOnline: 0,
                  scontoNegozio: 0,
                },
              ],
        socioOnav: data.socioOnav,
        numeroTessera: data.numeroTessera || undefined,
        sezione: data.sezione?.data?.id,
        stato: data.stato,
        scadenzaContratto: data.scadenzaContratto,
        // null (non undefined): con undefined react-hook-form, nel reset,
        // mantiene il valore già presente nell'input (es. coordinate del record
        // aperto in precedenza) e la pagina mostra coordinate non salvate.
        latitude: data.latitude ?? null,
        longitude: data.longitude ?? null,
        revisionNote: data.revisionNote || undefined,
      };
      reset(formData);
    }
  }, [fidelity, reset]);

  const { mutate: updateMutation, isLoading: isUpdating } = useMutation(
    'updateFidelity',
    async (formData: FidelityRequest) => {
      const { data, coordinates } = await withResolvedCoordinates(formData);
      let socioOnav = false;
      let warning: string | null = null;
      if (data.numeroTessera) {
        const res = await validateFidelityCard({
          token,
          numeroTessera: Number(data.numeroTessera),
        });
        if (!res.valid) {
          warning =
            'Attenzione questa tessera non esiste oppure non è attiva! Controlla di aver digitato correttamente il numero tessera oppure attiva la tessera per essere visibile su onav.it.';
        }
        socioOnav = res.socioOnav;
      }
      const result = await updateFidelity({
        id: id!,
        body: { ...data, socioOnav },
        token,
      });
      return { result, warning, coordinates };
    },
    {
      onSuccess: ({ warning, coordinates }) => {
        queryClient.invalidateQueries(['fidelity', id]);
        setTesseraWarning(warning);
        toast.success('Fidelity aggiornato con successo');
        if (coordinates === 'not-found') {
          toast.warning(COORDINATES_NOT_FOUND_WARNING);
        }
      },
      onError: () => {
        toast.error("Errore durante l'aggiornamento del Fidelity");
      },
    }
  );

  const { mutate: approveMutation, isLoading: isApproving } = useMutation(
    'approveFidelity',
    async () => {
      return approveFidelity({ id: id!, token });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fidelity', id]);
        toast.success('Fidelity approvato con successo');
      },
      onError: (e: any) => {
        toast.error(e?.message || "Errore durante l'approvazione del Fidelity");
      },
    }
  );

  const { mutate: rejectMutation, isLoading: isRejecting } = useMutation(
    'rejectFidelity',
    (note: string) => rejectFidelity({ id: id!, token, note }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fidelity', id]);
        toast.success('Fidelity messo in revisione');
        setIsRejectModalOpen(false);
      },
      onError: () => {
        toast.error('Errore durante la revisione del Fidelity');
      },
    }
  );

  const onSubmit = (data: FidelityRequest) => {
    updateMutation(data);
  };

  const SaveActions = () => (
    <div className='flex gap-4'>
      <PrimaryButton outline onClick={() => setIsCancelModalOpen(true)}>
        {canEdit ? 'Annulla' : 'Indietro'}
      </PrimaryButton>

      {canEdit && (
        <PrimaryButton
          type='submit'
          isLoading={isUpdating}
          disabled={isUpdating}
        >
          Salva modifiche
        </PrimaryButton>
      )}
    </div>
  );

  if (isLoading) {
    return <div>Caricamento...</div>;
  }

  if (!fidelity?.data) {
    return <div>Fidelity non trovato</div>;
  }

  const currentStatus = fidelity.data.attributes.stato;
  const isInApproval = currentStatus === FidelityStatus.IN_APPROVAZIONE;
  const canEdit =
    !isReadOnly && currentStatus !== FidelityStatus.IN_APPROVAZIONE;

  return (
    <>
      <TitlePage title='Modifica Fidelity' />

      <div className='flex justify-between items-center mb-6'>
        <H1Styled>Modifica Fidelity</H1Styled>
        <InlineBadge
          label={getStatusLabel(currentStatus)}
          color={getStatusColor(currentStatus)}
        />
      </div>

      {canApprove && isInApproval && (
        <div className='bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6'>
          <div className='flex'>
            <div className='flex-shrink-0'>
              <svg
                className='h-5 w-5 text-yellow-400'
                viewBox='0 0 20 20'
                fill='currentColor'
              >
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12zm0-9a1 1 0 011 1v4a1 1 0 11-2 0V8a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-yellow-800'>
                Richiesta in attesa di approvazione
              </h3>
              <div className='mt-2 text-sm text-yellow-700'>
                <p>
                  Questo Fidelity è in attesa di approvazione. Puoi approvarlo o
                  metterlo in revisione.
                </p>
              </div>
              <div className='mt-4 flex gap-2'>
                <PrimaryButton
                  onClick={() => handleSubmit(() => approveMutation())()}
                  isLoading={isApproving}
                  disabled={isApproving}
                  small
                >
                  Approva
                </PrimaryButton>
                <PrimaryButton
                  onClick={() => setIsRejectModalOpen(true)}
                  disabled={isApproving}
                  small
                >
                  Da rivedere
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {currentStatus === FidelityStatus.BOZZA && !isReadOnly && (
        <div className='bg-gray-50 border border-gray-200 rounded-md p-4 mb-6'>
          <div className='flex'>
            <div className='flex-shrink-0'>
              <svg
                className='h-5 w-5 text-gray-400'
                viewBox='0 0 20 20'
                fill='currentColor'
              >
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12zm0-9a1 1 0 011 1v4a1 1 0 11-2 0V8a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-gray-800'>
                Fidelity in bozza
              </h3>
              <div className='mt-2 text-sm text-gray-700'>
                <p>
                  Questo Fidelity è ancora in bozza. L'utente deve firmare il
                  contratto per poter essere approvato.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {currentStatus === FidelityStatus.RIFIUTATO && !isReadOnly && (
        <div className='bg-red-50 border border-red-200 rounded-md p-4 mb-6'>
          <div className='flex'>
            <div className='flex-shrink-0'>
              <svg
                className='h-5 w-5 text-red-400'
                viewBox='0 0 20 20'
                fill='currentColor'
              >
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12zm0-9a1 1 0 011 1v4a1 1 0 11-2 0V8a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-red-800'>
                Fidelity in revisione
              </h3>
              <div className='mt-2 text-sm text-red-700'>
                <p>
                  Questo Fidelity è stato messo in revisione. Puoi apportare le
                  modifiche necessarie e rinviarlo per l'approvazione.
                </p>
              </div>
              <div className='mt-4'>
                <PrimaryButton
                  onClick={() => {
                    const data = watch();
                    data.stato = FidelityStatus.IN_APPROVAZIONE;
                    handleSubmit(() => updateMutation(data))();
                  }}
                  isLoading={isUpdating}
                  disabled={isUpdating}
                  small
                >
                  Rinvia per approvazione
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      <FidelityContractSection
        fidelity={fidelity?.data?.attributes}
      />

      <form onSubmit={handleSubmit(onSubmit)} className='space-y-8'>
        <div className='flex justify-between'>
          <div className='flex gap-4'></div>
          <SaveActions />
        </div>
        <FidelityForm
          register={register}
          control={control}
          errors={errors}
          watch={watch}
          setValue={setValue}
          token={token}
          profile={profile}
          isReadOnly={!canEdit}
          isEditMode={true}
          tesseraWarning={tesseraWarning}
        />

        <div className='flex justify-end pt-4'>
          <SaveActions />
        </div>
      </form>

      <BaseModal
        isOpen={isRejectModalOpen}
        toggle={() => setIsRejectModalOpen(false)}
        title='Metti in revisione'
        confirmButtonText='Conferma'
        cancelButtonText='Annulla'
        onConfirm={() => {
          const noteValue = watch('revisionNote') || '';
          if (noteValue.trim()) {
            rejectMutation(noteValue);
          } else {
            toast.error('Inserisci una nota per la revisione');
          }
        }}
        onCancel={() => {
          setIsRejectModalOpen(false);
        }}
        isLoading={isRejecting}
      >
        <div className='mt-4'>
          <TextareaPresentational
            register={register}
            label='Nota per la revisione*'
            id='revisionNote'
            name='revisionNote'
            error={errors.note}
          />
        </div>
      </BaseModal>

      <BaseModal
        isOpen={isCancelModalOpen}
        toggle={() => setIsCancelModalOpen(false)}
        title='Conferma annullamento'
        subtitle='Sei sicuro di voler annullare le modifiche? Tutte le modifiche non salvate andranno perse.'
        confirmButtonText='Sì, annulla'
        cancelButtonText='Continua a modificare'
        onConfirm={() => navigate('/fidelity')}
        onCancel={() => setIsCancelModalOpen(false)}
      />
    </>
  );
};

export default ModificaFidelity;
